import { Injectable } from "@angular/core";
import { Host } from "@interfaces/host";
import { observe } from "@web/util/loading/loading";
import { toast } from "@web/util/toast";
import { tr } from "@util/tr";
import { DEFAULT_ORDER_CURRENCY } from "@util/currency/order-currency";
import { computePreorderPayableCents } from "@util/order-payable";
import { PreorderFilters, PreordersService } from "./preorders.service";
import { Preorder } from "@interfaces/dws/preorder";
import { saveXLSX } from "@util/save-xlsx";
import { Parser } from "json2csv";
import { save } from "@util/save-as";
import { formatOrderDateForExport, formatUtcTimestampLocal } from "@util/utc-timestamp";

const MAX_EXPORT_ROWS = 5000;

type NamedChannel = { id: string; name?: string };

@Injectable({
  providedIn: "root",
})
export class ExportPreordersService {
  constructor(private preordersService: PreordersService) {}

  export(
    format: "xlsx" | "csv",
    company: Host.Company,
    filters: PreorderFilters,
    channels: NamedChannel[]
  ): void {
    toast(tr("We are preparing your export..."));
    observe(this.preordersService.list(filters, 0, MAX_EXPORT_ROWS)).subscribe(
      (page) => {
        switch (format) {
          case "csv":
            this.convertToCsv(page.content, channels);
            break;
          case "xlsx":
            this.convertToXlsx(page.content, channels);
            break;
        }
      },
      () => toast(tr("Error"))
    );
  }

  private pricelistTypeLabel(): string {
    return tr("Price List");
  }

  private pricelistName(channelId: string | number | undefined, channels: NamedChannel[]): string {
    if (channelId === undefined || channelId === null) {
      return "";
    }
    return channels.find((c) => c.id === String(channelId))?.name || "";
  }

  private formatInstant(iso: string | undefined): string {
    return iso ? formatUtcTimestampLocal(iso, "dd/MM/yyyy HH:mm") : "";
  }

  private formatOrderDate(row: Preorder): string {
    if (row.orderDateTime) {
      return this.formatInstant(row.orderDateTime as string);
    }
    return formatOrderDateForExport(row.preorderDate);
  }

  private registrationDate(row: Preorder): string {
    if (row.registrationDateTime) {
      return this.formatInstant(row.registrationDateTime as string);
    }
    return formatOrderDateForExport(row.preorderDate);
  }

  private lastModifiedDate(row: Preorder): string {
    if (row.lastModifiedDateTime) {
      return this.formatInstant(row.lastModifiedDateTime as string);
    }
    return this.formatOrderDate(row);
  }

  private totalAmount(row: Preorder): number {
    return computePreorderPayableCents(row) / 100;
  }

  private statusLabel(status: string | undefined): string {
    return status ? tr(status) : "";
  }

  private paymentTermsLabel(row: Preorder): string {
    if (row.paymentTermsKind === "paid_online") {
      return tr("Paid online");
    }
    return tr("Paid at the winery");
  }

  private exportHeaders() {
    return {
      orderId: tr("selfOrderList.column.orderId"),
      customerContact: tr("selfOrderList.column.customerContact"),
      email: tr("Email"),
      orderDate: tr("selfOrderList.column.orderDate"),
      registrationDate: tr("selfOrderList.column.registrationDate"),
      lastModified: tr("selfOrderList.column.lastModified"),
      units: tr("orderList.column.units"),
      total: tr("orderList.column.total"),
      currency: tr("Currency"),
      pricelist: tr("selfOrderList.filter.pricelist"),
      pricelistName: tr("selfOrderList.export.pricelistName"),
      payment: tr("selfOrderList.column.payment"),
      status: tr("orderList.column.status"),
    };
  }

  private rowToRecord(row: Preorder, channels: NamedChannel[]): Record<string, string | number> {
    const h = this.exportHeaders();
    return {
      [h.orderId]: row.id ?? "",
      [h.customerContact]: row.contactName ?? "",
      [h.email]: row.email ?? "",
      [h.orderDate]: this.formatOrderDate(row),
      [h.registrationDate]: this.registrationDate(row),
      [h.lastModified]: this.lastModifiedDate(row),
      [h.units]: row.productsQuantity ?? "",
      [h.total]: this.totalAmount(row),
      [h.currency]: row.currency || DEFAULT_ORDER_CURRENCY,
      [h.pricelist]: this.pricelistTypeLabel(),
      [h.pricelistName]: this.pricelistName(row.channelId, channels),
      [h.payment]: this.paymentTermsLabel(row),
      [h.status]: this.statusLabel(row.status),
    };
  }

  private convertToXlsx(rows: Preorder[], channels: NamedChannel[]): void {
    const data = rows.map((row) => this.rowToRecord(row, channels));
    const h = this.exportHeaders();
    saveXLSX(data, "self-orders.xlsx", { monetaryColumnHeaders: [h.total] });
  }

  private convertToCsv(rows: Preorder[], channels: NamedChannel[]): void {
    const h = this.exportHeaders();
    const fields = [
      { value: (r: Preorder) => r.id ?? "", label: h.orderId },
      { value: (r: Preorder) => r.contactName ?? "", label: h.customerContact },
      { value: (r: Preorder) => r.email ?? "", label: h.email },
      { value: (r: Preorder) => this.formatOrderDate(r), label: h.orderDate },
      { value: (r: Preorder) => this.registrationDate(r), label: h.registrationDate },
      { value: (r: Preorder) => this.lastModifiedDate(r), label: h.lastModified },
      { value: (r: Preorder) => r.productsQuantity ?? "", label: h.units },
      { value: (r: Preorder) => this.totalAmount(r), label: h.total },
      { value: (r: Preorder) => r.currency || DEFAULT_ORDER_CURRENCY, label: h.currency },
      { value: () => this.pricelistTypeLabel(), label: h.pricelist },
      { value: (r: Preorder) => this.pricelistName(r.channelId, channels), label: h.pricelistName },
      { value: (r: Preorder) => this.paymentTermsLabel(r), label: h.payment },
      { value: (r: Preorder) => this.statusLabel(r.status), label: h.status },
    ];
    const parser = new Parser({ delimiter: ",", fields });
    const csvRows = parser.parse(rows);
    save("\uFEFF" + csvRows).as("self-orders.csv", "text/csv; charset=utf-8");
  }
}
